import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { getApiUrl } from "../config/api";

type AdminUser = {
  id: string;
  email: string;
  role: "admin" | "superadmin";
} | null;

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  isInitializing: boolean;
  adminUser: AdminUser;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  authFetch: (
    input: RequestInfo | URL,
    init?: RequestInit
  ) => Promise<Response>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [adminUser, setAdminUser] = useState<AdminUser>(null);

  useEffect(() => {
    // Проверяем, есть ли сохраненная авторизация при загрузке
    const savedToken = localStorage.getItem("admin-access-token");
    const savedAdminUser = localStorage.getItem("admin-user");
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
    }
    if (savedAdminUser) {
      try {
        const parsed = JSON.parse(savedAdminUser);
        setAdminUser(parsed);
      } catch {}
    }
    setIsInitializing(false);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(getApiUrl("/api/auth/admin/login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!response.ok) {
        let message = "Ошибка входа в админку";
        try {
          const data = await response.json();
          message = data?.message || message;
        } catch (_) {
          // игнорируем, если ответ не JSON
        }
        throw new Error(message);
      }
      const data = await response.json();
      setToken(data.access_token);
      localStorage.setItem("admin-access-token", data.access_token);
      const minimalUser = {
        id: data.user.id,
        email: data.user.email,
        role: data.user.role,
      };
      setAdminUser(minimalUser);
      localStorage.setItem("admin-user", JSON.stringify(minimalUser));
      setIsAuthenticated(true);
    } catch (e) {
      // сбрасываем возможные предыдущие токены
      localStorage.removeItem("admin-access-token");
      localStorage.removeItem("admin-user");
      setToken(null);
      setAdminUser(null);
      setIsAuthenticated(false);
      throw e;
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setToken(null);
    setAdminUser(null);
    localStorage.removeItem("admin-access-token");
    localStorage.removeItem("admin-user");
  };

  const authFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const headers: Record<string, string> = {
      ...(init?.headers as any),
    };
    const t = token || localStorage.getItem("admin-access-token");
    if (t) headers.Authorization = `Bearer ${t}`;
    const res = await fetch(input, { ...init, headers });
    if (res.status === 401 || res.status === 403) {
      // Сбрасываем и уводим на логин
      logout();
      // Жесткий редирект, чтобы очистить состояние роутера
      window.location.replace("/admin/login");
      throw new Error("Unauthorized");
    }
    return res;
  };

  const value: AuthContextType = {
    isAuthenticated,
    token,
    isInitializing,
    adminUser,
    login,
    logout,
    authFetch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
